<template>
  <div class="flex h-full w-full min-w-0 flex-col gap-4 overflow-y-auto overflow-x-hidden p-4 pt-20 xl:pt-8">
    <!-- Header (always visible, including during the loading skeleton) -->
    <div>
      <h1 class="text-2xl font-bold text-eblack-900 sm:text-3xl">
        {{ $t("services.auto.servicios") }}
      </h1>
      <p class="mt-1 text-sm text-eblack-500">{{ $t("services.auto.subtitulo") }}</p>
    </div>

    <!-- KPIs (Airbnb & Apple inspired) -->
    <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 sm:gap-4">
      <!-- KPI 1: Total Servicios -->
      <div
        class="group relative flex flex-col justify-between overflow-hidden rounded-2xl border border-ewhite-200 bg-white/90 p-4.5 shadow-[0_1px_3px_rgba(0,0,0,0.03),0_6px_16px_rgba(0,0,0,0.02)] backdrop-blur-sm transition-all duration-300 ease-out hover:-translate-y-0.5 hover:border-ewhite-300 hover:shadow-[0_4px_12px_rgba(0,0,0,0.05),0_12px_28px_rgba(0,0,0,0.05)] sm:p-5"
      >
        <div class="flex items-center justify-between">
          <div
            class="flex h-10 w-10 items-center justify-center rounded-xl border border-eblack-100 bg-eblack-50 text-eblack-700 shadow-sm transition-transform duration-300 group-hover:scale-105"
          >
            <IconBriefcase class="h-5 w-5" stroke="1.75" />
          </div>
          <span
            class="inline-flex items-center rounded-full border border-eblack-100 bg-eblack-50 px-2.5 py-0.5 text-[11px] font-semibold text-eblack-600"
          >
            {{ $t("services.auto.servicios") }}
          </span>
        </div>

        <div class="mt-4">
          <p class="text-3xl font-extrabold tracking-tight text-eblack-900 leading-none">
            {{ totalCount }}
          </p>
          <p class="mt-1.5 text-[13px] font-medium text-eblack-500">
            {{ $t("services.auto.total_de_servicios") }}
          </p>
        </div>
      </div>

      <!-- KPI 2: Activos -->
      <div
        class="group relative flex flex-col justify-between overflow-hidden rounded-2xl border border-ewhite-200 bg-white/90 p-4.5 shadow-[0_1px_3px_rgba(0,0,0,0.03),0_6px_16px_rgba(0,0,0,0.02)] backdrop-blur-sm transition-all duration-300 ease-out hover:-translate-y-0.5 hover:border-emerald-200 hover:shadow-[0_4px_12px_rgba(16,185,129,0.06),0_12px_28px_rgba(16,185,129,0.08)] sm:p-5"
      >
        <div class="flex items-center justify-between">
          <div
            class="flex h-10 w-10 items-center justify-center rounded-xl border border-emerald-100 bg-emerald-50 text-emerald-600 shadow-sm transition-transform duration-300 group-hover:scale-105"
          >
            <IconCircleCheck class="h-5 w-5" stroke="1.75" />
          </div>
          <span
            class="inline-flex items-center gap-1.5 rounded-full border border-emerald-200/60 bg-emerald-50 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700"
          >
            <span class="h-1.5 w-1.5 rounded-full bg-emerald-500"></span>
            {{ activePercentage }}%
          </span>
        </div>

        <div class="mt-4">
          <p class="text-3xl font-extrabold tracking-tight text-eblack-900 leading-none">
            {{ activeCount }}
          </p>
          <p class="mt-1.5 text-[13px] font-medium text-eblack-500">
            {{ $t("services.auto.activos") }}
          </p>
          <p v-if="inactiveCount > 0" class="mt-1 flex items-center gap-1 text-xs font-medium text-amber-600">
            <span class="h-1.5 w-1.5 rounded-full bg-amber-500"></span>
            {{ $t("services.auto.n_inactivos", { n: inactiveCount }) }}
          </p>
        </div>
      </div>

      <!-- KPI 3: Con horarios -->
      <div
        class="group relative flex flex-col justify-between overflow-hidden rounded-2xl border border-ewhite-200 bg-white/90 p-4.5 shadow-[0_1px_3px_rgba(0,0,0,0.03),0_6px_16px_rgba(0,0,0,0.02)] backdrop-blur-sm transition-all duration-300 ease-out hover:-translate-y-0.5 hover:border-sky-200 hover:shadow-[0_4px_12px_rgba(14,165,233,0.06),0_12px_28px_rgba(14,165,233,0.08)] sm:p-5"
      >
        <div class="flex items-center justify-between">
          <div
            class="flex h-10 w-10 items-center justify-center rounded-xl border border-sky-100 bg-sky-50 text-sky-600 shadow-sm transition-transform duration-300 group-hover:scale-105"
          >
            <IconCalendarEvent class="h-5 w-5" stroke="1.75" />
          </div>
          <span
            class="inline-flex items-center gap-1.5 rounded-full border border-sky-200/60 bg-sky-50 px-2.5 py-0.5 text-[11px] font-semibold text-sky-700"
          >
            <span class="h-1.5 w-1.5 rounded-full bg-sky-500"></span>
            {{ scheduledPercentage }}%
          </span>
        </div>

        <div class="mt-4">
          <p class="text-3xl font-extrabold tracking-tight text-eblack-900 leading-none">
            {{ scheduledCount }}
          </p>
          <p class="mt-1.5 text-[13px] font-medium text-eblack-500">
            {{ $t("services.auto.con_horarios") }}
          </p>
          <p v-if="unscheduledCount > 0" class="mt-1 flex items-center gap-1 text-xs font-medium text-eblack-400">
            <span class="h-1.5 w-1.5 rounded-full bg-sky-500"></span>
            {{ $t("services.auto.n_sin_configurar", { n: unscheduledCount }) }}
          </p>
        </div>
      </div>

      <!-- KPI 4: Comisión promedio -->
      <div
        class="group relative flex flex-col justify-between overflow-hidden rounded-2xl border border-ewhite-200 bg-white/90 p-4.5 shadow-[0_1px_3px_rgba(0,0,0,0.03),0_6px_16px_rgba(0,0,0,0.02)] backdrop-blur-sm transition-all duration-300 ease-out hover:-translate-y-0.5 hover:border-eorange-200 hover:shadow-[0_4px_12px_rgba(239,78,32,0.06),0_12px_28px_rgba(239,78,32,0.08)] sm:p-5"
      >
        <div class="flex items-center justify-between">
          <div
            class="flex h-10 w-10 items-center justify-center rounded-xl border border-eorange-100 bg-eorange-50 text-eorange-500 shadow-sm transition-transform duration-300 group-hover:scale-105"
          >
            <IconPercentage class="h-5 w-5" stroke="1.75" />
          </div>
          <span
            class="inline-flex items-center gap-1 rounded-full border border-eorange-200/60 bg-eorange-50 px-2.5 py-0.5 text-[11px] font-semibold text-eorange-600"
          >
            <IconTrendingUp class="h-3 w-3" stroke="2" />
            {{ $t("services.auto.comision_label") }}
          </span>
        </div>

        <div class="mt-4">
          <p class="text-3xl font-extrabold tracking-tight text-eblack-900 leading-none">
            {{ avgCommissionLabel }}
          </p>
          <p class="mt-1.5 text-[13px] font-medium text-eblack-500">
            {{ $t("services.auto.comision_promedio") }}
          </p>
        </div>
      </div>
    </div>

    <!-- Filter + primary action -->
    <div
      v-if="isInitialLoading || services.length > 0"
      class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-start"
    >
      <div class="relative w-full sm:w-72">
        <IconSearch class="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-eblack-400" />
        <input
          v-model="search"
          type="text"
          name="search"
          class="w-full rounded-lg border-ewhite-300 py-2 pl-9 pr-4 text-sm focus:border-eorange-400 focus:ring-eorange-400"
          :placeholder="$t('services.auto.buscar_servicios')"
        />
      </div>

      <button
        v-if="canManageServices"
        type="button"
        class="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-eorange-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-eorange-600 sm:w-auto"
        @click="showServiceForm()"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.75"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
          <path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0" />
          <path d="M9 12h6" />
          <path d="M12 9v6" />
        </svg>
        <span>{{ $t("services.auto.agregar_servicio") }}</span>
      </button>
    </div>

    <!-- Loading skeleton: only the card grid is replaced -->
    <div
      v-if="isInitialLoading"
      class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-x-6 gap-y-8"
    >
      <ServiceCardSkeleton v-for="n in 6" :key="n" />
    </div>

    <!-- Service grid -->
    <div
      v-else-if="filteredServices.length > 0"
      class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-x-6 gap-y-8"
    >
      <ServiceCard
        v-for="service in filteredServices"
        :key="service.id"
        :service="service"
        :setService="showServiceForm"
        :onDelete="handleDeleteService"
        :onViewSchedule="showServiceSchedule"
        :commissionRange="commissionRanges[service.id] ?? undefined"
        :priceRange="priceRanges[service.id] ?? undefined"
      />
    </div>

    <!-- No results for the current search -->
    <div
      v-else-if="services.length > 0"
      class="flex flex-col items-center justify-center rounded-2xl border border-ewhite-200 bg-ewhite-100/60 py-16 text-center"
    >
      <div class="flex h-16 w-16 items-center justify-center rounded-full bg-ewhite-200">
        <IconSearch class="h-7 w-7 text-eblack-300" />
      </div>
      <p class="mt-4 text-sm text-eblack-500">{{ $t("services.auto.no_se_encontraron_servicios") }}</p>
    </div>

    <!-- Empty state -->
    <div
      v-else
      class="flex flex-col items-center justify-center rounded-2xl border border-ewhite-200 px-8 py-16 text-center"
    >
      <div class="flex h-[72px] w-[72px] items-center justify-center rounded-full bg-ewhite-100">
        <IconBriefcase class="h-8 w-8 text-eblack-300" stroke="{1.5}" />
      </div>
      <h2 class="mt-5 text-lg font-semibold text-eblack-900">
        {{ $t("services.auto.todavia_no_hay_servicios") }}
      </h2>
      <p class="mt-2 max-w-sm text-sm leading-relaxed text-eblack-500">
        {{ $t("services.auto.crea_tu_primer_servicio") }}
      </p>
      <button
        v-if="canManageServices"
        type="button"
        class="mt-6 inline-flex items-center justify-center gap-2 rounded-lg bg-eorange-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-eorange-600"
        @click="showServiceForm()"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.75"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
          <path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0" />
          <path d="M9 12h6" />
          <path d="M12 9v6" />
        </svg>
        <span>{{ $t("services.auto.agregar_servicio") }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import { i18n } from "@/i18n";
import ServiceCard from "./components/ServiceCard.vue";
import ServiceCardSkeleton from "./components/ServiceCardSkeleton.vue";
import ServiceFormSidebar from "./components/ServiceFormSidebar.vue";
import ServiceScheduleModal from "./components/ServiceScheduleModal.vue";
import { useOverlay } from "@/services/overlay-service";
import { createSidebarConfig, createModalConfig } from "@/utils/overlay-utils";
import {
  IconBriefcase,
  IconSearch,
  IconCircleCheck,
  IconCalendarEvent,
  IconPercentage,
  IconTrendingUp,
} from "@tabler/icons-vue";
import { computed } from "vue";
import { useAbility } from "@/domains/auth";
import { useServiceStore } from "@domains/service/stores/service.store";
import { useToast } from "vue-toastification";
import { extractApiErrorMessage } from "@/utils/api-error";
import { calendarRepository } from "@domains/scheduling/repositories/calendar.repository";
import { computeCommissionRange } from "@domains/scheduling/utils/commission-range";
import { computePriceRange } from "@domains/scheduling/utils/price-range";

export default {
  name: "ServiceIndex",
  data() {
    return {
      search: "",
      selectedService: null,
      services: [],
      showDetails: false,
      isInitialLoading: true,
      commissionRanges: {},
      priceRanges: {},
      scheduledCount: 0,
      avgCommission: null,
    };
  },
  components: {
    ServiceCard,
    ServiceCardSkeleton,
    IconBriefcase,
    IconSearch,
    IconCircleCheck,
    IconCalendarEvent,
    IconPercentage,
    IconTrendingUp,
  },
  setup() {
    const overlayService = useOverlay();
    const { can } = useAbility();
    const serviceStore = useServiceStore();
    const toast = useToast();
    const canManageServices = computed(() => can("manage", "all") || can("manage", "Service"));
    return { overlayService, serviceStore, canManageServices, toast };
  },
  computed: {
    totalCount() {
      return this.services.length;
    },
    activeCount() {
      return this.services.filter((s) => !s.state || s.state === "ACT").length;
    },
    inactiveCount() {
      return this.totalCount - this.activeCount;
    },
    activePercentage() {
      if (!this.totalCount) return 0;
      return Math.round((this.activeCount / this.totalCount) * 100);
    },
    unscheduledCount() {
      return this.totalCount - this.scheduledCount;
    },
    scheduledPercentage() {
      if (!this.totalCount) return 0;
      return Math.round((this.scheduledCount / this.totalCount) * 100);
    },
    avgCommissionLabel() {
      if (this.avgCommission == null) return "—";
      return `${this.avgCommission.toFixed(1).replace(".", ",")}%`;
    },
    filteredServices() {
      const value = this.search.trim().toLowerCase();
      if (!value) return this.services;
      return this.services.filter((service) =>
        service.name.toLowerCase().includes(value)
      );
    },
  },
  methods: {
    showServiceForm(service = null, handleShow = false) {
      const readOnly = !this.canManageServices;
      this.selectedService = service;
      this.showDetails = handleShow || readOnly;

      this.overlayService.open(
        createSidebarConfig({
          component: ServiceFormSidebar,
          props: {
            service: this.selectedService,
            showDetails: this.showDetails,
            readOnly,
            loadServices: this.initData,
            overlayService: this.overlayService
          },
          onClose: () => {
            this.selectedService = null;
            this.showDetails = false;
          }
        })
      );
    },
    showServiceSchedule(service) {
      this.overlayService.open(
        createModalConfig({
          component: ServiceScheduleModal,
          size: "md",
          props: { service },
        })
      );
    },
    async handleDeleteService(id) {
      try {
        await this.serviceStore.deleteService(id);
        await this.initData();
        this.toast.success(i18n.global.t("services.auto.servicio_eliminado"));
      } catch (error) {
        console.error(error);
        this.toast.error(extractApiErrorMessage(error, "Error al eliminar el servicio"));
      }
    },
    async initData() {
      try {
        const data = await this.serviceStore.fetchServices(true);
        this.services = data;
        this.fetchServiceRates(data);
      } catch (error) {
        console.error(error);
      } finally {
        this.isInitialLoading = false;
      }
    },
    async fetchServiceRates(services) {
      const results = await Promise.allSettled(
        services
          .filter((s) => s.id != null)
          .map(async (s) => [s.id, await calendarRepository.getAvailabilityRules(s.id)])
      );

      const commission = {};
      const price = {};
      let scheduled = 0;
      const commissionSamples = [];

      for (const result of results) {
        if (result.status !== "fulfilled") continue;
        const [id, rules] = result.value;

        if (rules.length > 0) scheduled += 1;

        const commissionRange = computeCommissionRange(rules);
        if (commissionRange != null) commission[id] = commissionRange;

        const priceRange = computePriceRange(rules);
        if (priceRange != null) price[id] = priceRange;

        for (const rule of rules) {
          if (rule.commission_type === "PERCENTAGE") {
            commissionSamples.push(rule.commission_amount);
          }
        }
      }

      this.commissionRanges = commission;
      this.priceRanges = price;
      this.scheduledCount = scheduled;
      this.avgCommission =
        commissionSamples.length > 0
          ? commissionSamples.reduce((sum, v) => sum + v, 0) / commissionSamples.length
          : null;
    },
  },
  mounted() {
    this.initData();
  },
};
</script>

<style lang="scss" scoped></style>
